import { IBriefInfluencerV2 } from "@shared/lib/types/briefInfluencerV2";
import {
  applyHeaderCheckboxChange,
  applyRowCheckboxChange,
  getHeaderCheckboxState,
  getVisibleSelectedKeys,
  isSelectAllRowSelected,
} from "@shared/lib/hooks/selectAllCheckboxSelection";
import { Checkbox } from "@shared/ui/Checkbox";
import type { TableProps } from "antd/es/table";
import classNames from "classnames";
import { useMemo } from "react";
import styles from "./styles.module.scss";

type UseBriefRowSelectionArgs = {
  rows: IBriefInfluencerV2[];
  totalCount: number;
  selectAll: boolean;
  checkedList: string[];
  uncheckList: string[];
  setSelectAll: (value: boolean) => void;
  setCheckedList: (list: string[]) => void;
  setUncheckList: (list: string[]) => void;
  selectedRows: IBriefInfluencerV2[];
  setSelectedRows: (rows: IBriefInfluencerV2[]) => void;
  hasActiveSearch: boolean;
  highlightedRecordId?: string;
};

export function useBriefRowSelection({
  rows,
  totalCount,
  selectAll,
  checkedList,
  uncheckList,
  setSelectAll,
  setCheckedList,
  setUncheckList,
  selectedRows,
  setSelectedRows,
  hasActiveSearch,
  highlightedRecordId,
}: UseBriefRowSelectionArgs): TableProps<IBriefInfluencerV2>["rowSelection"] {
  const visibleIds = useMemo(() => rows.map((row) => row._id), [rows]);
  const total = totalCount > 0 ? totalCount : rows.length;
  const setters = {
    setSelectAll,
    setCheckedList,
    setUncheckList,
  };

  return {
    selectedRowKeys: getVisibleSelectedKeys(
      visibleIds,
      selectAll,
      checkedList,
      uncheckList
    ),
    columnWidth: 30,
    renderCell: (_, record) => (
      <div className={styles.selectionCell}>
        <Checkbox
          id={record._id}
          checked={isSelectAllRowSelected(
            record._id,
            selectAll,
            checkedList,
            uncheckList
          )}
          onChange={(event) => {
            applyRowCheckboxChange({
              rowId: record._id,
              checked: event.target.checked,
              selectAll,
              checkedList,
              uncheckList,
              total,
              hasActiveSearch,
              setters,
            });

            if (event.target.checked) {
              setSelectedRows(
                selectedRows.some((row) => row._id === record._id)
                  ? selectedRows
                  : [...selectedRows, record]
              );
              return;
            }
            setSelectedRows(
              selectedRows.filter((row) => row._id !== record._id)
            );
          }}
          className={classNames({
            [styles.highlightedCheckbox]: highlightedRecordId === record._id,
          })}
        />
      </div>
    ),
    columnTitle: () => {
      const { checked, indeterminate } = getHeaderCheckboxState({
        selectAll,
        checkedList,
        uncheckList,
        visibleIds,
        total,
        hasActiveSearch,
      });

      return (
        <Checkbox
          checked={checked}
          indeterminate={indeterminate}
          onChange={(event) => {
            applyHeaderCheckboxChange({
              checked: event.target.checked,
              selectAll,
              checkedList,
              uncheckList,
              visibleIds,
              hasActiveSearch,
              setters,
            });

            if (!hasActiveSearch && !event.target.checked) {
              setSelectedRows([]);
              return;
            }

            if (hasActiveSearch && !selectAll) {
              if (event.target.checked) {
                const nextRows = [
                  ...selectedRows.filter(
                    (row) => !visibleIds.includes(row._id)
                  ),
                  ...rows,
                ];
                setSelectedRows(nextRows);
                return;
              }
              setSelectedRows(
                selectedRows.filter((row) => !visibleIds.includes(row._id))
              );
            }
          }}
        />
      );
    },
  };
}
